import Box from "@mui/material/Box";
import { useCallback, useEffect, useState } from "react";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { useSnackbar } from "notistack";
import AlertMessages from "common/Alert/AlertMessages";
import { useTranslation } from "react-i18next";
import {
  Button,
  CircularProgress,
  Grid,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import { checkPrivileges } from "Helpers/CheckPrivileges";
import { useNavigate } from "react-router-dom";
import FormDialog from "common/Modal/Modal";
import ComponentHeaderTwo from "common/ComponentHeaderTwo/ComponentHeaderTwo";
import { ReactComponent as Vehicle } from "../../assets/SideBarIcons/vehicle.svg";
import BackendPaginationTable from "common/Table/TableWithPagination";
import VehicleSearchHeader from "components/Vehicle/VehicleSearchHeader";
import {
  CommonProfileService,
  VehicleProfileService,
} from "subModule/src/core/services";
import {
  ChangeStatusVehicleResponse,
  VehicleResponse,
} from "subModule/src/services/vehicle/interface";
import { DeleteData, Wrapper } from "subModule/src/type";
import Loader from "layout/Loader";
import { useGetVehicleService } from "subModule/src/services/vehicle/useVehicle";
import VehicleSearchHeaderMenu from "components/Vehicle/VehicleSearchHeaderMenu";
import { themes } from "theme/themes";
import { useSelector } from "react-redux";
import { brandingInfo } from "features/server/brandingSlice";
import {
  getSortParamFromColumn,
  SortOrder,
  VEHICLE_SORT_FIELD_MAP,
} from "Helpers/buildApiSortParam";

const VEHICLE_SORTABLE_COLUMNS = ["lastServiceDate"];
interface SearchFormData {
  [key: string]: string | any;
}
interface VehicleProps {
  userId?: number;
  accountId?: number;
  type?: "client" | "customer";
}
export default function VehicleListing(props: VehicleProps) {
  const navigate = useNavigate();
  const [openDeleteModal, setDeleteModal] = useState(false);
  const [pageNo, setPageNo] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(props?.type ? 5 : 20);
  const [loading, setLoading] = useState<boolean>(false);
  const [filter, setFilter] = useState<string[]>(["platenumber"]);
  const [search, setSearch] = useState<any>({});
  const [sort, setSort] = useState<string | undefined>();
  const [sortOrderBy, setSortOrderBy] = useState("");
  const [sortOrder, setSortOrder] = useState<SortOrder>("asc");
  const [row, setRow] = useState<VehicleResponse>({});
  const [statusModal, setStatusModal] = useState(false);
  const [downloading, setDownloading] = useState<boolean>(false);
  const [searchFormData, setSearchFormData] = useState<SearchFormData>({
    platenumber: "",
    vin: "",
    makeTitle: "",
    modelTitle: "",
    customerName: "",
    customerPhone: "",
  });
  const {
    data: tableData,
    refetch,
    isFetching,
  } = useGetVehicleService({
    pageNumber: pageNo,
    size: pageSize,
    ...search,
    sort,
    ...(props?.accountId && { accountId: props.accountId }),
    ...(props?.userId && { userId: props.userId }),
  });
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const branding = useSelector(brandingInfo);
  const themeColors =
    branding?.attributes?.themeId === 100
      ? branding?.attributes?.customTheme
      : (themes as any)[branding?.attributes?.themeId || 3];
  const fetchData = useCallback(() => {
    refetch().then((res: any) => {
      if (!res?.data?.pages[0]?.success)
        enqueueSnackbar(
          <AlertMessages
            text={res?.data?.pages[0]?.message || "Something went wrong"}
          />,
          { variant: "error" },
        );
    });
  }, []);
  useEffect(() => {
    fetchData();
  }, [pageNo, pageSize, search, sort]);
  const handleChangePage = (pageNo: number) => {
    setPageNo(pageNo + 1);
  };
  const handleChangeRows = (pageSize: number) => {
    setPageSize(pageSize);
    setPageNo(1);
  };
  const onEdit = (row: any) => navigate("/vehicle/" + row.id);
  const showRowView = (row: any) => {
    if (!props?.type) navigate("/vehicle/" + row.id);
  };
  const onDelete = (row: any) => {
    setDeleteModal(true);
    setRow(row);
  };
  const clearSearch = () => {
    setFilter(["platenumber"]);
    setSearchFormData({
      platenumber: "",
      vin: "",
      makeTitle: "",
      modelTitle: "",
      customerName: "",
      customerPhone: "",
    });
    if (Object.keys(search).length) {
      setSearch({});
    }
  };
  const handleChangeFilter = (event: SelectChangeEvent<typeof filter>) => {
    const {
      target: { value },
    } = event;
    setFilter(typeof value === "string" ? value.split(",") : value);
    setSearchFormData((prevFormData) =>
      Object.fromEntries(
        Object.keys(prevFormData).map((key) => [
          key,
          value.includes(key) ? prevFormData[key] : "",
        ]),
      ),
    );
    if (!value.length && Object.keys(search).length) {
      clearSearch();
    }
  };
  const handleChangeSearch = (field: string, value: any) => {
    const val = value;
    setSearchFormData((prevData) => ({
      ...prevData,
      [field]: val,
    }));
  };
  const handleSubmitSearch = () => {
    const filteredSearch: Record<string, string> = {};
    Object.entries(searchFormData).forEach(([key, value]) => {
      if (value) {
        filteredSearch[key] = value?.trim();
      }
    });
    if (
      Object.keys(filteredSearch).length > 0 &&
      Object.values(filteredSearch).some((value) => value)
    ) {
      setSearch(filteredSearch);
      setPageNo(1);
      setPageSize(pageSize);
    }
  };
  const handleSortChange = (orderBy: string, order: SortOrder) => {
    const nextSort = getSortParamFromColumn(
      orderBy,
      order,
      VEHICLE_SORT_FIELD_MAP,
    );
    if (!nextSort) return;
    setSortOrderBy(orderBy);
    setSortOrder(order);
    setSort(nextSort);
    setPageNo(1);
  };
  const onChangeStatus = (row: any) => {
    setRow(row);
    setStatusModal(true);
  };
  const actionslist = [
    { label: "edit", onClick: onEdit, icon: <EditIcon /> },
    { label: "delete", onClick: onDelete, icon: <DeleteIcon /> },
  ];
  const adjustActions = (actions: any[]) => {
    return checkPrivileges("ROLE_VEHICLE_UPDATE")
      ? checkPrivileges("ROLE_VEHICLE_ARCHIVE")
        ? actions
        : actions.filter((action) => action.label !== "delete")
      : checkPrivileges("ROLE_VEHICLE_ARCHIVE")
        ? actions.filter((action) => action.label !== "edit")
        : [];
  };
  const DownloadVehicles = () => {
    CommonProfileService.exportEntityWithParams(
      {
        ...search,
        ...(props?.accountId && { accountId: props.accountId }),
        ...(props?.userId && { userId: props.userId }),
      },
      "vehicles/export/excel",
    )
      .then((res: Blob) => {
        const blob = new Blob([res], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        const url = window.URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `Vehicles.xlsx`);
        document.body.appendChild(link);
        link.click();

        link.remove();
        window.URL.revokeObjectURL(url);
        enqueueSnackbar(<AlertMessages text={t("downloadedSuccessfully")} />, {
          variant: "success",
        });
      })
      .catch((err) => {
        console.error("===", err);
        enqueueSnackbar(<AlertMessages statusCode={0} />, { variant: "error" });
      })
      .finally(() => {
        setDownloading(false);
      });
  };
  const markFavourite = (row: any) => {
    setLoading(true);
    CommonProfileService.markAsFavourite({
      id: row.id,
      typeName: "vehicles",
      favorite: !row.favorite,
    })
      .then((response: Wrapper) => {
        if (response.success) {
          refetch();
          enqueueSnackbar(
            <AlertMessages
              statusCode={response.status}
              text={
                row.favorite ? t("removedFromFavorites") : t("addedToFavorites")
              }
            />,
            { variant: "success" },
          );
        } else {
          enqueueSnackbar(<AlertMessages statusCode={response.status} />, {
            variant: "error",
          });
        }
      })
      .catch((err: any) => {
        console.error("===", err);
        enqueueSnackbar(
          <AlertMessages
            text={err?.data?.message || err || "Something went wrong"}
          />,
          { variant: "error" },
        );
      })
      .finally(() => setLoading(false));
  };
  return (
    <Box>
      {props?.type ? null : (
        <ComponentHeaderTwo
          showBtn={true}
          showSaveBtn={false}
          onClick={() => navigate("/vehicle/create")}
          heading={t("vehicles")}
          icon={<Vehicle height={28} width={28} />}
          btnText={"create"}
          btnType={"create"}
          showCreateBtn={checkPrivileges("ROLE_VEHICLE_CREATE")}
        />
      )}
      {isFetching || loading ? (
        <Loader />
      ) : (
        <BackendPaginationTable
          columns={[
            { id: "favorite", label: "", numeric: false, width: "48px" },
            {
              id: "plateNumber",
              label: "plateNumber",
              numeric: false,
              width: "14%",
            },
            { id: "stateName", label: "state", numeric: false, width: "9%" },
            {
              id: "clientContact",
              label: props?.type === "customer" ? "customerInfo" : "clientInfo",
              numeric: false,
              width: "17%",
            },
            {
              id: "makeModel",
              label: "makeModel",
              numeric: false,
              width: "12%",
            },
            { id: "vin", label: "VIN", numeric: false, width: "17%" },
            {
              id: "lastServiceDate",
              label: "lastServiceDate",
              numeric: false,
              width: "12%",
            },
            ...(checkPrivileges("ROLE_VEHICLE_ACTIVE") && !props?.type
              ? [
                  {
                    id: "active",
                    label: "status",
                    numeric: false,
                    width: "8%",
                  },
                ]
              : []),
            ...(adjustActions(actionslist).length && !props?.type
              ? [
                  {
                    id: "actions",
                    label: "actions",
                    numeric: false,
                    width: "5%",
                  },
                ]
              : []),
          ]}
          data={tableData?.pages[0]?.data?.content || []}
          pageNo={pageNo}
          pageSize={pageSize}
          tableData={tableData?.pages[0]?.data || {}}
          showCheckbox={false}
          showActions={true}
          enableSorting={true}
          serverSideSorting={true}
          sortOrderBy={sortOrderBy}
          sortOrder={sortOrder}
          onSortChange={handleSortChange}
          sortableColumnIds={VEHICLE_SORTABLE_COLUMNS}
          showHeader={true}
          showSearch={true}
          showFilter={true}
          showCreateButton={true}
          actions={adjustActions(actionslist)}
          handleChangePage={handleChangePage}
          handleChangeRows={handleChangeRows}
          onChangeStatus={onChangeStatus}
          rowClick={checkPrivileges("ROLE_VEHICLE_UPDATE")}
          showRowView={showRowView}
          loadingSearch={false}
          markFavourite={markFavourite}
          headerComponent={
            <>
              <Box className="table-header-search">
                <VehicleSearchHeader
                  searchFormData={searchFormData}
                  handleChangeFilter={handleChangeFilter}
                  handleChangeSearch={handleChangeSearch}
                  handleSubmitSearch={handleSubmitSearch}
                  clearSearch={clearSearch}
                  {...{ filter, search }}
                />
              </Box>
              {checkPrivileges("ROLE_VEHICLE_EXPORT") ||
              (checkPrivileges("ROLE_VEHICLE_IMPORT") && !props?.type) ? (
                <Box className="table-header-actions">
                  {checkPrivileges("ROLE_VEHICLE_EXPORT") ? (
                    <Button
                      id="import-button"
                      variant="contained"
                      disableElevation
                      onClick={() => {
                        setDownloading(true);
                        DownloadVehicles();
                      }}
                      className={`bg-white font-Saira font-[600] text-[13px] px-4 py-1 cursor-pointer rounded-[6px]`}
                      sx={{
                        outline: "2px solid",
                        color: themeColors?.secondary,
                        borderColor: themeColors?.secondary,
                        height: 34,
                        minHeight: 34,
                      }}
                      disabled={downloading}
                    >
                      {downloading ? (
                        <CircularProgress
                          sx={{
                            color: themeColors?.secondary,
                            marginRight: "8px",
                          }}
                          size={16}
                        />
                      ) : null}
                      {t("export")}
                    </Button>
                  ) : null}
                  {checkPrivileges("ROLE_VEHICLE_IMPORT") && !props?.type ? (
                    <VehicleSearchHeaderMenu refetch={refetch} />
                  ) : null}
                </Box>
              ) : null}
            </>
          }
        />
      )}
      {openDeleteModal && (
        <FormDialog
          open={openDeleteModal}
          onClose={() => setDeleteModal(false)}
          title={t("delete") + " " + t("vehicle")}
          // icon={<div><Home/></div>}
          content={
            <Grid container>
              <Grid item xs={12}>
                <Typography>
                  {row?.jobCardId || row?.workOrderId
                    ? t("deletingVehicleDeletesJCWo")
                    : t("Are you sure you want to delete?")}
                </Typography>
              </Grid>
            </Grid>
          }
          saveFunction={() => {
            setDeleteModal(false);
            setLoading(true);
            VehicleProfileService.deleteVehicle((row as any).id)
              .then((response: DeleteData) => {
                if (response.success) {
                  if (
                    tableData?.pages[0]?.data?.content?.length === 1 &&
                    pageNo > 1
                  )
                    setPageNo(1);
                  else refetch();
                  enqueueSnackbar(
                    <AlertMessages
                      statusCode={response.status}
                      text={
                        t("vehicle") +
                        " " +
                        t("deleted") +
                        " " +
                        t("successfully")
                      }
                    />,
                    { variant: "success" },
                  );
                } else {
                  enqueueSnackbar(
                    <AlertMessages statusCode={response.status} />,
                    { variant: "error" },
                  );
                }
              })
              .catch((err: any) => {
                console.error("===", err);
                enqueueSnackbar(
                  <AlertMessages
                    text={err?.data?.message || err || "Something went wrong"}
                  />,
                  { variant: "error" },
                );
              })
              .finally(() => setLoading(false));
          }}
          submitButtonText="Delete"
        />
      )}
      {statusModal && (
        <FormDialog
          open={statusModal}
          onClose={() => setStatusModal(false)}
          title="Change Status"
          // icon={<div><Home/></div>}
          content={
            <Grid container>
              <Grid item xs={12}>
                <Typography>{t(`Are you sure?`)}</Typography>
              </Grid>
            </Grid>
          }
          saveFunction={() => {
            setStatusModal(false);
            setLoading(true);
            VehicleProfileService.changeStatusVehicle(
              (row as any).id,
              row.active ? "inactive" : "active",
              {},
            )
              .then((response: ChangeStatusVehicleResponse) => {
                if (response.success) {
                  refetch();
                  enqueueSnackbar(
                    <AlertMessages
                      statusCode={response.status}
                      text={
                        t("status") +
                        " " +
                        t("changed") +
                        " " +
                        t("successfully")
                      }
                    />,
                    { variant: "success" },
                  );
                } else {
                  enqueueSnackbar(
                    <AlertMessages statusCode={response.status} />,
                    { variant: "error" },
                  );
                }
              })
              .catch((err: any) => {
                console.error("===", err);
                enqueueSnackbar(
                  <AlertMessages
                    text={err?.data?.message || err || "Something went wrong"}
                  />,
                  { variant: "error" },
                );
              })
              .finally(() => setLoading(false));
          }}
          submitButtonText="OK"
        />
      )}
    </Box>
  );
}
